import {gql} from '@apollo/client';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';

import type {MCPServerPingQuery, MCPServerPingQueryVariables} from './__generated/MCPServerPingQuery';

export const MCP_SERVER_PING_QUERY = gql`
  query MCPServerPingQuery($serverId: StrictString!) @skipAudit @engComponent(name: LLM) {
    mcpServerPing(serverId: $serverId) {
      isConnected
      needsRedirect
      redirectUrl
    }
  }
`;

export interface MCPPingStatus {
  isConnected: boolean;
  needsRedirect: boolean;
  redirectUrl?: string | null;
}

interface UseMCPPingOptions {
  serverId: string;
  enabled: boolean;
  pollingInterval?: number; // in milliseconds, default 5 minutes
}

interface UseMCPPingResult {
  status: MCPPingStatus | null;
  isLoading: boolean;
  error: boolean;
  refetch: () => void;
}

/**
 * Hook to ping an MCP server and poll for connection status.
 *
 * Uses Apollo Client's polling mechanism to periodically check the server status.
 *
 * @param options - Configuration options for the ping
 * @returns The current ping status, loading state, and any errors
 */
export function useMCPPing(options: UseMCPPingOptions): UseMCPPingResult {
  const {serverId, enabled, pollingInterval = 5 * 60 * 1000} = options; // Default 5 minutes

  const result = useSkippableBenchlingQuery<MCPServerPingQuery, MCPServerPingQueryVariables>(
    MCP_SERVER_PING_QUERY,
    {
      variables: {serverId},
      skip: !enabled,
      pollInterval: enabled ? pollingInterval : undefined,
      fetchPolicy: 'network-only', // Always fetch fresh data
      notifyOnNetworkStatusChange: true,
    }
  );

  if (result.type === QueryResultType.SKIPPED) {
    return {
      status: null,
      isLoading: false,
      error: false,
      refetch: result.refetch,
    };
  } else if (result.type === QueryResultType.ERROR) {
    return {
      status: {
        isConnected: false,
        needsRedirect: false,
      },
      isLoading: false,
      error: true,
      refetch: result.refetch,
    };
  } else if (result.type === QueryResultType.PENDING) {
    return {
      status: null,
      isLoading: true,
      error: false,
      refetch: result.refetch,
    };
  } else {
    return {
      status: {
        isConnected: result.data.mcpServerPing.isConnected,
        needsRedirect: result.data.mcpServerPing.needsRedirect,
        redirectUrl: result.data.mcpServerPing.redirectUrl,
      },
      isLoading: false,
      error: false,
      refetch: result.refetch,
    };
  }
}
